import * as Sentry from '@sentry/react'
import { type AxiosError } from 'axios'

/**
 * fingerprint 用に、URL のパスの数値 ID を `:id` に置き換える。
 * ID を残すと、同じ API の同じエラーが操作した対象ごとに別の Issue に割れ、
 * 件数と影響人数が積み上がらない（#6167）
 */
const toRoutePattern = (path: string): string =>
  path
    .split('/')
    .map((segment) => (/^\d+$/.test(segment) ? ':id' : segment))
    .join('/')

/**
 * SentryにHTTPエラーを送信するインターセプター（全環境有効）
 */
export const sentryErrorInterceptor = (error: AxiosError): Promise<never> => {
  // 4xx/5xx を error として送信して可視性を担保する
  const status = error.response?.status
  if (typeof status === 'number' && status >= 400) {
    Sentry.withScope((scope) => {
      // PII対策: URLクエリを除去し、可能な限り最小限のメタ情報のみ送信する
      const originalUrl = String(error.config?.url ?? '')
      const safeUrl = originalUrl.split('?')[0]

      scope.setTag('http_status', status)
      scope.setTag('http_method', error.config?.method)
      scope.setTag('api_endpoint', safeUrl)
      // 400(Bad Request) は error、それ以外の 4xx は warning、5xx は error
      if (status === 400) {
        scope.setLevel('error')
      } else if (status >= 401 && status < 500) {
        scope.setLevel('warning')
      } else if (status >= 500) {
        scope.setLevel('error')
      }
      // スタックトレースの axios 由来に依存しないよう、API毎にグルーピング。
      // 調査で実際の ID が要るので、タグと context の URL は ID を残したままにする
      scope.setFingerprint([
        'api-error',
        String(status),
        String(error.config?.method),
        toRoutePattern(safeUrl)
      ])

      // PII対策: レスポンスボディやヘッダーの生データは送信しない
      scope.setContext('http_error', {
        url: safeUrl,
        method: error.config?.method,
        status
      })

      Sentry.captureException(error)
    })
  }

  return Promise.reject(error)
}
